import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiXCircle, FiEye, FiFileText, FiPrinter, FiLink, FiLock, FiUnlock
} from 'react-icons/fi';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import LinkedDocNumbers from '../../components/LinkedDocNumbers';
import ClosedOrderNote from '../../components/ClosedOrderNote';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG } from '../../utils/locationRequired';
import { formatAmount } from '../../utils/documentNumbering';
import LineDiscountRows from '../../components/LineDiscountRows';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import {
  createTransaction,
  updateTransaction,
  deleteTransaction,
  updateTransactionStatus,
  getTransactions,
  getTransaction,
  closeOrder,
  reopenOrder,
  orderPendingLines
} from '../../utils/transactionHelper';
import PageLayout from '../../components/PageLayout';
import SendWhatsAppButton from '../../components/SendWhatsAppButton';
import VendorSelect from '../../components/VendorSelect';
import { useVendorPricing, getVendorPrice } from '../../utils/vendorPricingHelper';
import ParentDocLink from '../../components/ParentDocLink';
import ItemSelect from '../../components/ItemSelect';
import QuickAddItemModal from '../../components/QuickAddItemModal';
import QuickAddVendorModal from '../../components/QuickAddVendorModal';
import { isFutureDate } from '../../utils/validationHelpers';
import { getItemStockCached } from '../../utils/stockFlowHelper';
import { getVendorsCached, getItemsCached, getEmployeesCached, getJobsCached, getLocationsCached } from '../../utils/masterDataStore';
import LineUnitPicker from '../../components/LineUnitPicker';
import TwoUnitQtyBox, { TwoUnitNote } from '../../components/TwoUnitQtyBox';
import { seedLineUnitFromItem, applyUnitChangeToLine, twoUnitSwitchQty } from '../../utils/unitConversion';
import { percentForDiscountAmount } from '../../utils/discountPercent';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import AttachmentPanel from '../../components/AttachmentPanel';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow, DiscountInput,
  SortableTh, SortContext
} from '../../components/SharedStyles';

const TRAN_TYPE = 'PO';

// Footer summary spec — aggregated server-side in paged mode (~few reads),
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Draft', 'Approved', 'Completed']
};

const EMPTY_LINE = {
  itemId: '',
  itemCode: '',
  itemName: '',
  description: '',
  unitOfMeasure: '',
  unitCode: '',
  quantity: 1,
  unitPrice: 0,
  discountPercent: 0,
  discountAmount: 0,
  taxable: false,
  taxRate: 0,
  taxAmount: 0,
  lineTotal: 0,
  receivedQty: 0,
  pendingQty: 0,
  trackingType: 'None',
  serialNumbers: []
};

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Approved: colors.info.main,
  'Partially Received': '#9C27B0',
  Completed: colors.success.main,
  Closed: '#546E7A', // closed by hand with some quantity left (see Close Order)
  Cancelled: colors.error.main,
};

const PurchaseOrder = () => {
  const location = useLocation();
  const { activeCompany, currentUser, companyUser, activeFinancialYear, activeBranch, userProfile, isModuleEnabled } = useAuth();
  const { canEdit, canDelete, canCancel, canChange } = usePagePermissions('transactions', 'purchaseOrder');
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => { if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [activeCompany]);

  const isAdmin = companyUser?.role === 'admin';

  // View mode
  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');

  // List state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: purchaseOrders,
    loading,
    refresh: loadPurchaseOrders,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  // Form state
  const [editingPO, setEditingPO] = useState(null);
  const [viewingPO, setViewingPO] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    docNumber: '',
    docDate: new Date().toISOString().split('T')[0],
    vendorId: '',
    vendorName: '',
    referenceNo: '',
    deliveryDate: '',
    deliveryAddress: '',
    paymentTerms: '',
    currency: 'PKR',
    discountAmount: 0,
    notes: '',
    termsConditions: '',
    status: 'Draft',
    employeeId: '', employeeName: '', jobId: '', jobName: '',
    locationId: '', locationName: '',
    // Optional parent link to a Purchase Requisition (REQ). Mirrors the
    // GRN-from-PO pattern: vendor + lines auto-fill on selection, lines lock
    // while the parent link is set, transactionHelper writes bidirectional
    // childIds on save.
    parentId: '',
    parentDocNumber: '',
    parentTranType: ''
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);

  // Reference data
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [stockBalances, setStockBalances] = useState({});

  // Quick-add (create a master record without leaving the purchase order).
  // quickAddIndex remembers WHICH line asked for the new item; the pending*
  // states defer selection until the new row is actually in state, so the
  // normal auto-fill runs exactly as a manual pick would.
  const [showQuickAddItem, setShowQuickAddItem] = useState(false);
  const [quickAddIndex, setQuickAddIndex] = useState(null);
  const [pendingItemSelect, setPendingItemSelect] = useState(null);
  const [showQuickAddVendor, setShowQuickAddVendor] = useState(false);
  const [pendingVendorSelect, setPendingVendorSelect] = useState(null);

  // Approved REQs available as a parent for this PO. We keep already-linked
  // REQs out of the dropdown by checking each REQ's childIds — once a REQ
  // has produced a PO, hide it (re-using the same REQ for two POs would
  // need a "split" workflow we haven't designed yet).
  const [approvedRequisitions, setApprovedRequisitions] = useState([]);

  // PO list now comes from useDocListPagination above (loadPurchaseOrders =
  // its refresh). The old full-history getTransactions load lived here.

  // Tier 1 (2026-07-27, Firebase cost): vendors/items come from the device-local
  // master cache (getMaster* → IndexedDB, 0 reads per open with a throttled
  // background change-check) instead of a full getDocs on every open. Each
  // screen keeps its own client-side filter/sort on the returned rows.
  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getVendorsCached(activeCompany.id);
      setVendors([...rows].sort((a, b) => (a.vendorName || '').localeCompare(b.vendorName || '')));
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  }, [activeCompany]);

  // Load items
  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) {
      console.error('Error loading items:', error);
    }
  }, [activeCompany]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading employees:', error); }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading jobs:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  // Firebase cost: stock is fetched ONE ITEM AT A TIME, on demand. The old
  // mount-time load scanned the company's ENTIRE stockFlow history just to draw
  // badges the user may never look at. Now the dropdown shows a "stock?" chip
  // and this runs only when the user taps it.
  const requestItemStock = useCallback(async (itemId) => {
    if (!activeCompany?.id || !itemId) return;
    try {
      const qty = await getItemStockCached(activeCompany.id, itemId, {
        branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : undefined
      });
      setStockBalances(prev => ({ ...prev, [itemId]: qty }));
    } catch (e) { console.error('Error loading item stock:', e); }
  }, [activeCompany, activeBranch]);

  // ── D3 (2026-07-23, Firebase cost): the loader below feeds ONLY the form's
  // "Link to Requisition" dropdown — nothing on the list view uses it. It used
  // to run on every page open, scanning the full REQ history. It now loads
  // ONCE, lazily, the first time a form is actually opened. List browsing costs
  // nothing.
  const formDataLoadedRef = useRef(false);

  // Load approved Purchase Requisitions that haven't already spawned a PO.
  // Only Approved REQs are eligible — Draft/Submitted/Rejected are excluded
  // by design so a PO never references unapproved demand.
  const loadApprovedRequisitions = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const data = await getTransactions(activeCompany.id, {
        tranTypeCode: 'REQ',
        status: 'Approved',
        ...(activeBranch && activeBranch.id !== 'all' ? { branchId: activeBranch.id } : {})
      });
      // Hide REQs that already have a child PO. We still allow editing a PO
      // whose parent is consumed (handleEdit injects the saved REQ into the
      // dropdown explicitly — see below).
      setApprovedRequisitions(data.filter(r => !r.childIds || r.childIds.length === 0));
    } catch (error) { console.error('Error loading approved requisitions:', error); }
  }, [activeCompany, activeBranch]);

  // Loads the form-only dropdown data once per page session (see D3 note above).
  const ensureFormData = useCallback(async () => {
    if (formDataLoadedRef.current || !activeCompany?.id) return;
    formDataLoadedRef.current = true;
    try {
      await Promise.all([loadApprovedRequisitions()]);
    } catch (error) {
      formDataLoadedRef.current = false; // let a later form open retry
      console.error('Error loading form link data:', error);
    }
  }, [activeCompany, loadApprovedRequisitions]);

  // NOTE: the PO list itself loads inside useDocListPagination — not here.
  // D3: the requisition loader is NOT here anymore — it loads lazily on first
  // form open (ensureFormData), since only the form uses it.
  useEffect(() => {
    loadVendors();
    loadItems();
    loadEmployees();
    loadJobs();
    loadLocations();
  }, [loadVendors, loadItems, loadEmployees, loadJobs, loadLocations]);

  // Open a specific PO when navigated here from another page (e.g. clicking
  // the "Linked PO" link from the Purchase Requisition view). With pagination
  // the target may not be in the loaded page — ensureDoc fetches that single
  // document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(transaction => {
      if (transaction) {
        setViewingPO(transaction);
        setViewMode('view');
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
  }, [location.state, ensureDoc]);

  // Filter orders
  const filteredOrders = useMemo(() => {
    let filtered = purchaseOrders;
    if (statusFilter !== 'All') {
      filtered = filtered.filter(po => po.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(po =>
        po.docNumber?.toLowerCase().includes(q) ||
        po.partyName?.toLowerCase().includes(q) ||
        po.referenceNo?.toLowerCase().includes(q)
      );
    }
    return sort.sortItems(filtered);
  }, [purchaseOrders, statusFilter, searchQuery, sort]);

  // Calculate a single line
  const calculateLine = (line) => {
    const amount = line.quantity * line.unitPrice;
    const discAmt = amount * (line.discountPercent / 100);
    const net = amount - discAmt;
    const taxAmt = line.taxable ? net * (line.taxRate / 100) : 0;
    return {
      ...line,
      discountAmount: Math.round(discAmt * 100) / 100,
      taxAmount: Math.round(taxAmt * 100) / 100,
      lineTotal: Math.round((net + taxAmt) * 100) / 100,
      pendingQty: line.quantity - (line.receivedQty || 0)
    };
  };

  // Header totals
  const totals = useMemo(() => {
    const lines = lineItems.map(calculateLine);
    const subtotal = lines.reduce((s, l) => s + (l.quantity * l.unitPrice - l.discountAmount), 0);
    const tax = lines.reduce((s, l) => s + l.taxAmount, 0);
    const disc = Number(formData.discountAmount) || 0;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      taxAmount: Math.round(tax * 100) / 100,
      headerDiscount: disc,
      totalAmount: Math.round((subtotal - disc + tax) * 100) / 100
    };
  }, [lineItems, formData.discountAmount]);

  // Form handlers
  const handleHeaderChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // A quick-added item lands here: push it into the local list, then let the
  // effect below select it on the line that asked for it once React has it.
  const handleQuickItemCreated = (newItem) => {
    setItems(prev => [...prev, newItem].sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    if (quickAddIndex != null) setPendingItemSelect({ index: quickAddIndex, itemId: newItem.id });
    setShowQuickAddItem(false);
    setQuickAddIndex(null);
  };

  useEffect(() => {
    if (pendingItemSelect && items.some(i => i.id === pendingItemSelect.itemId)) {
      handleLineChange(pendingItemSelect.index, 'itemId', pendingItemSelect.itemId);
      setPendingItemSelect(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, pendingItemSelect]);

  const handleQuickVendorCreated = (newVendor) => {
    setVendors(prev => [...prev, newVendor].sort((a, b) => (a.vendorName || '').localeCompare(b.vendorName || '')));
    setShowQuickAddVendor(false);
    setPendingVendorSelect(newVendor.id);
  };

  useEffect(() => {
    if (pendingVendorSelect && vendors.some(v => v.id === pendingVendorSelect)) {
      handleVendorChange(pendingVendorSelect);
      setPendingVendorSelect(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendors, pendingVendorSelect]);

  const handleVendorChange = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    setFormData(prev => ({
      ...prev,
      vendorId,
      vendorName: vendor?.vendorName || '',
      paymentTerms: vendor?.paymentTerms ? `${vendor.paymentTerms} days` : prev.paymentTerms,
      deliveryAddress: vendor?.address || prev.deliveryAddress,
      // Manually picking a vendor unlinks any parent REQ — the chosen vendor
      // may no longer match the requisition's preferred vendor.
      parentId: '',
      parentDocNumber: '',
      parentTranType: ''
    }));
  };

  const handleRequisitionChange = (reqId) => {
    if (!reqId) {
      setFormData(prev => ({ ...prev, parentId: '', parentDocNumber: '', parentTranType: '' }));
      setLineItems([{ ...EMPTY_LINE }]);
      return;
    }
    const req = approvedRequisitions.find(r => r.id === reqId);
    if (!req) return;

    // If every REQ line targets the same preferredVendorId, default the PO to
    // that vendor. Mixed vendors (or no preferred vendor) → leave the picker
    // blank so the user must choose explicitly.
    const reqLines = req.lines || [];
    const preferredIds = [...new Set(reqLines.map(l => l.preferredVendorId).filter(Boolean))];
    const uniformVendor = preferredIds.length === 1
      ? vendors.find(v => v.id === preferredIds[0]) || null
      : null;

    setFormData(prev => ({
      ...prev,
      parentId: reqId,
      parentDocNumber: req.docNumber || '',
      parentTranType: 'REQ',
      vendorId: uniformVendor?.id || prev.vendorId || '',
      vendorName: uniformVendor?.vendorName || prev.vendorName || '',
      paymentTerms: uniformVendor?.paymentTerms ? `${uniformVendor.paymentTerms} days` : prev.paymentTerms,
      deliveryAddress: uniformVendor?.address || prev.deliveryAddress,
      // Inherit REQ-level fields when present (locationName / required date can
      // double as expected delivery date).
      locationId: req.locationId || prev.locationId,
      locationName: req.locationName || prev.locationName,
      deliveryDate: prev.deliveryDate || req.requiredDate || ''
    }));

    // Carry every requisition line into the PO. Estimated price → unit price;
    // user can still edit per-line price/qty before approving the PO. We
    // intentionally drop preferredVendorId from the line — it's a hint at
    // the REQ stage, not part of the PO contract with one vendor.
    if (reqLines.length > 0) {
      setLineItems(reqLines.map(l => {
        const item = items.find(i => i.id === l.itemId);
        return {
          ...EMPTY_LINE,
          itemId: l.itemId || '',
          itemCode: l.itemCode || item?.itemCode || '',
          itemName: l.itemName || item?.itemName || '',
          description: l.description || l.itemName || item?.itemName || '',
          quantity: Number(l.quantity) || 0,
          unitPrice: Number(l.estimatedPrice) || 0,
          unitOfMeasure: l.unitOfMeasure || item?.unitOfMeasure || '',
          unitCode: l.unitCode || l.unitOfMeasure || item?.unitOfMeasure || '',
          trackingType: item?.trackingType || 'None',
          taxable: (item?.gstRate || 0) > 0,
          taxRate: item?.gstRate || 0
        };
      }));
    }
  };

  // Vendor-wise Items & Pricing (module purchases_vendor_pricing): the picked
  // vendor's price list. Empty — and nothing is read — while the module is off.
  const vendorPricing = useVendorPricing(activeCompany?.id, formData.vendorId, isModuleEnabled);

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Line discount can be entered as % OR as an amount (they sync), same as
      // Purchase Invoice. The % stays the stored driver; typing an amount
      // back-calculates the % so calculateLine keeps deriving the amount from it.
      if (field === 'discountAmountInput') {
        const gross = (Number(updated[index].quantity) || 0) * (Number(updated[index].unitPrice) || 0);
        const amt = Math.max(0, Number(value) || 0);
        delete updated[index].discountAmountInput; // not a saved field
        // Shortest % that gives back exactly the typed amount (4 decimals alone
        // could turn 70 into 69.99).
        updated[index].discountPercent = percentForDiscountAmount(gross, amt);
      }

      if (field === 'itemId' && value) {
        const item = items.find(i => i.id === value);
        // Serial numbers belong to the item they were entered for.
        if (prev[index].itemId !== value) { updated[index].serialNumbers = []; delete updated[index].serialText; }
        if (item) {
          updated[index].trackingType = item.trackingType || 'None';
          updated[index].itemCode = item.itemCode || '';
          updated[index].itemName = item.itemName || '';
          updated[index].description = item.itemName || '';
          updated[index].taxRate = item.gstRate || 0;
          updated[index].taxable = (item.gstRate || 0) > 0;
          const seeded = seedLineUnitFromItem(item, { priceField: 'purchasePrice', defaultUnitField: 'defaultPurchaseUnit' });
          updated[index] = { ...updated[index], ...seeded };
          // Vendor's own price wins over the item's purchase price.
          const vp = getVendorPrice(vendorPricing, value, item, updated[index].unitCode);
          if (vp !== null) updated[index].unitPrice = vp;
        }
      }

      if (field === 'unitCode' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'purchasePrice' });
          updated[index] = { ...updated[index], ...patch };
          const vp = getVendorPrice(vendorPricing, updated[index].itemId, item, value);
          if (vp !== null) updated[index].unitPrice = vp;
          // Show Two Units: switching "Rate per" between the item's two units keeps the same physical qty
          // (not on lines linked to another document — their qty is tracked against it).
          const twoQ = (formData.parentId || prev[index].sourceDocId || prev[index].poLineId) ? null
            : twoUnitSwitchQty(item, prev[index].unitCode || prev[index].unitOfMeasure, value, prev[index].quantity);
          if (twoQ !== null) updated[index].quantity = twoQ;
        }
      }

      updated[index] = calculateLine(updated[index]);
      return updated;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.itemId || (Number(line.quantity) > 0 && Number(line.unitPrice) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Add a new line item?')) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };

  const removeLine = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      docNumber: '', docDate: new Date().toISOString().split('T')[0],
      vendorId: '', vendorName: '', referenceNo: '', deliveryDate: '',
      deliveryAddress: '', paymentTerms: '', currency: activeCompany?.currencyCode || 'PKR',
      discountAmount: 0, notes: '', termsConditions: '', status: 'Draft',
      employeeId: '', employeeName: '', jobId: '', jobName: '',
      locationId: '', locationName: '',
      parentId: '', parentDocNumber: '', parentTranType: ''
    });
    setLineItems([{ ...EMPTY_LINE }]);
    setEditingPO(null);
  };

  const handleCreate = () => { resetForm(); ensureFormData(); setViewMode('form'); };

  const handleEdit = (po) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    ensureFormData(); // D3: load requisition link data on demand
    setEditingPO(po);
    setFormData({
      docNumber: po.docNumber || '',
      docDate: po.docDate || new Date().toISOString().split('T')[0],
      vendorId: po.partyId || '',
      vendorName: po.partyName || '',
      referenceNo: po.referenceNo || '',
      deliveryDate: po.deliveryDate || '',
      deliveryAddress: po.deliveryAddress || '',
      paymentTerms: po.paymentTerms || '',
      currency: po.currency || 'PKR',
      discountAmount: po.discountAmount || 0,
      notes: po.notes || '',
      termsConditions: po.termsConditions || '',
      status: po.status || 'Draft',
      employeeId: po.employeeId || '',
      employeeName: po.employeeName || '',
      jobId: po.jobId || '',
      jobName: po.jobName || '',
      locationId: po.locationId || '',
      locationName: po.locationName || '',
      parentId: po.parentId || '',
      parentDocNumber: po.parentDocNumber || '',
      parentTranType: po.parentTranType || ''
    });
    setLineItems(po.lines?.length > 0 ? po.lines.map(l => ({ ...EMPTY_LINE, ...l,
      trackingType: items.find(i => i.id === l.itemId)?.trackingType || l.trackingType || 'None',
      serialNumbers: Array.isArray(l.serialNumbers) ? l.serialNumbers : [] })) : [{ ...EMPTY_LINE }]);
    // If this PO is linked to a REQ, ensure that REQ shows up in the dropdown
    // even when our normal "exclude REQs that already have a child" filter
    // hides it. This lets the user re-pick / clear / view the link.
    if (po.parentId && po.parentTranType === 'REQ') {
      setApprovedRequisitions(prev => {
        if (prev.some(r => r.id === po.parentId)) return prev;
        // Fetch the linked REQ on demand and inject it into the list.
        getTransaction(activeCompany.id, po.parentId)
          .then(req => {
            if (req && req.tranTypeCode === 'REQ') {
              setApprovedRequisitions(curr => curr.some(r => r.id === req.id) ? curr : [req, ...curr]);
            }
          })
          .catch(() => { /* swallow; UI just won't show name until reload */ });
        return prev;
      });
    }
    setViewMode('form');
  };

  const handleView = (po) => { setViewingPO(po); setViewMode('view'); };

  // Save PO using unified transaction helper
  const handleSave = async (saveStatus) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (missingLocation(activeCompany, formData.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!formData.vendorId) { alert('Please select a vendor'); return; }
    if (!activeFinancialYear?.id) { alert('No active financial year'); return; }

    const validLines = lineItems.filter(l => l.description && l.quantity > 0 && l.unitPrice > 0);
    if (validLines.length === 0) {
      alert('Please add at least one line item with description, quantity, and price');
      return;
    }

    // Discount cap validation (max 50%)
    for (const line of validLines) {
      if (Number(line.discountPercent) > 50) {
        alert(`Discount on "${line.itemName || line.description}" exceeds 50%. Please review.`); return;
      }
    }

    // Serial numbers (same checks as Purchase Invoice). On an order they are
    // optional — they can be entered later on the GRN / invoice — but when
    // entered they must match the quantity, with no repeats.
    for (const line of validLines) {
      const serials = line.trackingType === 'Serial' && Array.isArray(line.serialNumbers) ? line.serialNumbers : [];
      if (!serials.length) continue;
      if (serials.length !== (line.quantity || 0)) {
        alert(`"${line.itemName || line.description}" requires exactly ${line.quantity} serial number(s). You entered ${serials.length}.`); return;
      }
      if (new Set(serials).size !== serials.length) {
        alert(`Duplicate serial numbers found for "${line.itemName || line.description}"`); return;
      }
    }

    // Future date warning
    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return;
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map(({ serialText, ...l }, i) => ({
        ...calculateLine(l),
        trackingType: l.trackingType || 'None',
        serialNumbers: l.trackingType === 'Serial' && Array.isArray(l.serialNumbers) ? l.serialNumbers : [],
        lineNumber: i + 1,
        unitCost: l.unitPrice // For stockFlow compatibility
      }));
      const status = saveStatus || formData.status || 'Draft';

      const transactionData = {
        tranTypeCode: TRAN_TYPE,
        docDate: formData.docDate,
        financialYearId: activeFinancialYear.id,
        branchId: activeBranch?.id || null,
        branchName: activeBranch?.branchName || null,
        partyType: 'vendor',
        partyId: formData.vendorId,
        partyName: formData.vendorName,
        referenceNo: formData.referenceNo,
        deliveryDate: formData.deliveryDate,
        deliveryAddress: formData.deliveryAddress,
        paymentTerms: formData.paymentTerms,
        currency: formData.currency,
        subtotal: totals.subtotal,
        discountAmount: totals.headerDiscount,
        taxAmount: totals.taxAmount,
        totalAmount: totals.totalAmount,
        employeeId: formData.employeeId,
        employeeName: formData.employeeName,
        jobId: formData.jobId,
        jobName: formData.jobName,
        locationId: formData.locationId,
        locationName: formData.locationName,
        notes: formData.notes,
        termsConditions: formData.termsConditions,
        status,
        lines: calculatedLines,
        // Optional REQ → PO parent link. transactionHelper.createTransaction
        // / updateTransaction call updateParentChildIds() to keep the REQ's
        // childIds in sync. Empty parentId is normalised to null below.
        parentId: formData.parentId || null,
        parentDocNumber: formData.parentDocNumber || '',
        parentTranType: formData.parentTranType || ''
      };

      let newTranData = null;

      if (editingPO) {
        // Update existing transaction
        await updateTransaction(activeCompany.id, editingPO.id, transactionData, currentUser.uid);

        // Update status if changing to Approved
        if (status !== editingPO.status) {
          await updateTransactionStatus(activeCompany.id, editingPO.id, status, currentUser.uid);
        }
      } else {
        // Create new transaction
        newTranData = await createTransaction(activeCompany.id, transactionData, currentUser.uid);

        // Apply status if not Draft (triggers sub-transaction generation)
        if (status !== 'Draft') {
          await updateTransactionStatus(activeCompany.id, newTranData.id, status, currentUser.uid);
        }
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedId = editingPO ? editingPO.id : newTranData?.id;
      const savedRecord = editingPO
        ? { ...editingPO, ...transactionData, id: savedId, status }
        : { ...newTranData, status };
      applyLocal(prev => editingPO
        ? prev.map(r => r.id === savedId ? savedRecord : r)
        : [savedRecord, ...prev]);

      setViewMode('list');
      resetForm();

      // Background refresh — reconciles with server data without blocking UI.
      loadPurchaseOrders().catch(err =>
        console.error('Background refresh failed:', err)
      );
    } catch (error) {
      console.error('Error saving purchase order:', error);
      alert('Error saving purchase order: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Delete PO
  const handleDelete = async (po) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (po.childIds && po.childIds.length > 0) { alert('Cannot delete - this PO has linked documents (GRN/Invoice)'); return; }
    if (!window.confirm(`Delete Purchase Order ${po.docNumber}?`)) return;
    try {
      await deleteTransaction(activeCompany.id, po.id);
      // D3: this may have freed the parent requisition — mark the form link-data
      // stale so the NEXT form open refetches it (zero reads here).
      formDataLoadedRef.current = false;
      await loadPurchaseOrders();
    } catch (error) {
      console.error('Error deleting PO:', error);
      alert('Error deleting purchase order: ' + error.message);
    }
  };

  // Cancel PO
  const handleCancel = async (po) => {
    if (!canCancel) { alert('You do not have permission to cancel.'); return; }
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (po.status === 'Completed' || po.status === 'Cancelled') {
      alert('This purchase order cannot be cancelled');
      return;
    }
    if (po.childIds && po.childIds.length > 0) {
      alert('Cannot cancel - this PO has linked documents');
      return;
    }
    if (!window.confirm(`Cancel Purchase Order ${po.docNumber}?`)) return;
    try {
      await updateTransactionStatus(activeCompany.id, po.id, 'Cancelled', currentUser.uid);
      await loadPurchaseOrders();
    } catch (error) {
      console.error('Error cancelling PO:', error);
    }
  };

  // Manual closing: the PO stays as entered (e.g. 100 ordered, 98 received)
  // but stops waiting for the rest — it leaves the "Link to PO" lists and the
  // rest shows as closed, not pending. Admin only. Reopen undoes it.
  const handleCloseOrder = async (po) => {
    if (!isAdmin) { alert('Only an admin can close an order.'); return; }
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    const left = orderPendingLines(po);
    const list = left.length
      ? left.slice(0, 12).map(p => `• ${p.label}: ${p.left}${p.unit ? ' ' + p.unit : ''} (ordered ${p.ordered}, received ${p.done})`).join('\n') + (left.length > 12 ? `\n…and ${left.length - 12} more` : '')
      : '(nothing is pending)';
    const reason = window.prompt(
      `Close Purchase Order ${po.docNumber}?\n\nStill pending — will be closed:\n${list}\n\n` +
      `The order stays as entered; it just stops waiting for the rest.\n\nReason (optional), then OK:`,
      ''
    );
    if (reason === null) return;
    try {
      const updated = await closeOrder(activeCompany.id, po.id, {
        userId: currentUser?.uid || null,
        userName: userProfile?.displayName || userProfile?.fullName || currentUser?.email || null,
        reason,
      });
      if (viewingPO && viewingPO.id === po.id) setViewingPO({ ...po, ...updated, id: po.id });
      await loadPurchaseOrders();
    } catch (error) {
      console.error('Error closing PO:', error);
      alert('Could not close the order: ' + (error?.message || 'unknown error'));
    }
  };

  const handleReopenOrder = async (po) => {
    if (!isAdmin) { alert('Only an admin can reopen an order.'); return; }
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!window.confirm(`Reopen Purchase Order ${po.docNumber}?\n\nIt will show again in "Link to PO" for the quantity still pending.`)) return;
    try {
      const updated = await reopenOrder(activeCompany.id, po.id, { userId: currentUser?.uid || null });
      if (viewingPO && viewingPO.id === po.id) setViewingPO({ ...po, ...updated, id: po.id });
      await loadPurchaseOrders();
    } catch (error) {
      console.error('Error reopening PO:', error);
      alert('Could not reopen the order: ' + (error?.message || 'unknown error'));
    }
  };

  // Print PO
  const handlePrint = (po) => {
    printDocument('purchaseOrder', po || viewingPO, activeCompany, printConfig, activeBranch);
  };

  // ===== RENDER =====

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox>
          <FiSearch />
          <input type="text" placeholder="Search by PO#, vendor, reference..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Approved">Approved</option>
            <option value="Partially Received">Partial</option>
            <option value="Completed">Completed</option>
            <option value="Closed">Closed</option>
            <option value="Cancelled">Cancelled</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Purchase Order</TranAddButton>}
        </FilterGroup>
      </ActionBar>

      {loading ? (
        <LoadingMessage>Loading purchase orders...</LoadingMessage>
      ) : filteredOrders.length === 0 ? (
        <TranEmptyState>
          <FiFileText size={48} />
          <h3>No Purchase Orders</h3>
          <p>{purchaseOrders.length === 0 ? 'Create your first purchase order' : 'No orders match your filters'}</p>
          {purchaseOrders.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Purchase Order</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer>
          <SortContext.Provider value={sort.ctxValue}>
          <Table>
            <thead>
              <tr>
                <SortableTh field="docNumber">PO Number</SortableTh>
                <SortableTh field="docDate">Date</SortableTh>
                {showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}
                <SortableTh field="partyName">Vendor</SortableTh>
                <SortableTh field="referenceNo">Reference</SortableTh>
                <SortableTh field="totalAmount" style={{ textAlign: 'right' }}>Amount</SortableTh>
                <Th style={{ textAlign: 'center' }}>Linked</Th>
                <SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
                <Th style={{ textAlign: 'center' }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(po => (
                <Tr key={po.id}>
                  <Td><strong>{po.docNumber}</strong></Td>
                  <Td>{po.docDate}</Td>
                  {showBranchCol && <Td>{po.branchName || '-'}</Td>}
                  <Td>{po.partyName}</Td>
                  <Td>{po.referenceNo || '-'}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 500 }}>
                    {po.currency || activeCompany?.currencyCode || 'PKR'} {formatAmount(po.totalAmount)}
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    {(po.parentId && po.parentDocNumber) || (po.childIds && po.childIds.length > 0) ? (
                      <>
                        {po.parentId && po.parentDocNumber && (
                          <LinkedBadge title={`From ${po.parentDocNumber}`}>
                            <FiLink /> {po.parentDocNumber}
                          </LinkedBadge>
                        )}
                        {po.childIds && po.childIds.length > 0 && (
                          <LinkedBadge>
                            <FiLink /> <LinkedDocNumbers ids={po.childIds} showIcon={false} color="inherit" style={{ fontSize: 'inherit' }} />
                          </LinkedBadge>
                        )}
                      </>
                    ) : '-'}
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <StatusBadge $color={STATUS_COLORS[po.status]}>{po.status}</StatusBadge>
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <ActionGroup>
                      <IconBtn title="View" onClick={() => handleView(po)}><FiEye /></IconBtn>
                      {canChange && po.status !== 'Cancelled' && po.status !== 'Closed' && <IconBtn title="Edit" onClick={() => handleEdit(po)}><FiEdit2 /></IconBtn>}
                      <IconBtn title="Print" onClick={() => handlePrint(po)}><FiPrinter /></IconBtn>
                      {po.status !== 'Draft' && po.status !== 'Cancelled' && (
                        <SendWhatsAppButton templateKey="purchaseOrder" doc={po} party={vendors.find(v => v.id === po.partyId)} variant="icon" />
                      )}
                      {canDelete && po.status !== 'Cancelled' && po.status !== 'Closed' && (!po.childIds || po.childIds.length === 0) && (
                        <IconBtn title="Delete" $danger onClick={() => handleDelete(po)}><FiTrash2 /></IconBtn>
                      )}
                      {isAdmin && canEdit && (po.status === 'Approved' || po.status === 'Partially Received') && (
                        <IconBtn title="Close order (stop waiting for the rest)" onClick={() => handleCloseOrder(po)}><FiLock /></IconBtn>
                      )}
                      {isAdmin && canEdit && po.status === 'Closed' && (
                        <IconBtn title="Reopen order" onClick={() => handleReopenOrder(po)}><FiUnlock /></IconBtn>
                      )}
                      {canCancel && canEdit && po.status !== 'Completed' && po.status !== 'Cancelled' && po.status !== 'Closed' && (!po.childIds || po.childIds.length === 0) && (
                        <IconBtn title="Cancel PO" $danger onClick={() => handleCancel(po)}><FiXCircle /></IconBtn>
                      )}
                    </ActionGroup>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          </SortContext.Provider>
        </TableContainer>
      )}

      <DocListPagination {...pagination} />

      <SummaryBar>
        <span>Total: <strong>{listFiltering ? filteredOrders.length : pagination.totalCount}</strong> order(s)</span>
        <span>
          Draft: <strong>{listSummary.counts.Draft || 0}</strong> |{' '}
          Approved: <strong>{listSummary.counts.Approved || 0}</strong> |{' '}
          Completed: <strong>{listSummary.counts.Completed || 0}</strong>
        </span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingPO) return null;
    const po = viewingPO;
    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingPO(null); }}>
            <FiArrowLeft /> Back to List
          </BackButton>
          <FormTitle>
            {po.docNumber}
            <StatusBadge $color={STATUS_COLORS[po.status]} style={{ marginLeft: 12 }}>{po.status}</StatusBadge>
          </FormTitle>
          <FormActions>
            {canChange && po.status !== 'Cancelled' && po.status !== 'Closed' && <SaveButton onClick={() => handleEdit(po)}><FiEdit2 /> Edit</SaveButton>}
            {isAdmin && canEdit && (po.status === 'Approved' || po.status === 'Partially Received') && <SaveButton $secondary onClick={() => handleCloseOrder(po)}><FiLock /> Close Order</SaveButton>}
            {isAdmin && canEdit && po.status === 'Closed' && <SaveButton $secondary onClick={() => handleReopenOrder(po)}><FiUnlock /> Reopen</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(po)}><FiPrinter /> Print</SaveButton>
          </FormActions>
        </FormHeader>

        {po.status === 'Closed' && <ClosedOrderNote order={po} doneLabel="received" />}

        <FormCard>
          <SectionTitle>Order Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>PO Number</label><span>{po.docNumber}</span></InfoItem>
            <InfoItem><label>Date</label><span>{po.docDate}</span></InfoItem>
            <InfoItem><label>Vendor</label><span>{po.partyName}</span></InfoItem>
            <InfoItem><label>Reference No.</label><span>{po.referenceNo || '-'}</span></InfoItem>
            <InfoItem><label>Delivery Date</label><span>{po.deliveryDate || '-'}</span></InfoItem>
            <InfoItem><label>Payment Terms</label><span>{po.paymentTerms || '-'}</span></InfoItem>
            <InfoItem><label>Delivery Address</label><span>{po.deliveryAddress || '-'}</span></InfoItem>
            <InfoItem><label>Currency</label><span>{po.currency}</span></InfoItem>
            <InfoItem><label>Employee</label><span>{po.employeeName || '-'}</span></InfoItem>
            <InfoItem><label>Job</label><span>{po.jobName || '-'}</span></InfoItem>
            <InfoItem><label>Location</label><span>{po.locationName || '-'}</span></InfoItem>
            {po.parentId && po.parentTranType === 'REQ' && (
              <InfoItem>
                <label>Requisition Ref</label>
                <span><ParentDocLink parentDocNumber={po.parentDocNumber} parentTranType="REQ" parentId={po.parentId} /></span>
              </InfoItem>
            )}
            {po.childIds && po.childIds.length > 0 && (
              <InfoItem><label>Linked Documents</label><span><LinkedDocNumbers ids={po.childIds} max={0} showIcon={false} /></span></InfoItem>
            )}
          </InfoGrid>
        </FormCard>

        <FormCard>
          <SectionTitle>Line Items</SectionTitle>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>#</Th><Th>Description</Th>
                  <Th style={{ textAlign: 'right' }}>Qty</Th>
                  <Th style={{ textAlign: 'right' }}>Unit Price</Th>
                  <Th style={{ textAlign: 'right' }}>Disc %</Th>
                  <Th style={{ textAlign: 'right' }}>Tax</Th>
                  <Th style={{ textAlign: 'right' }}>Total</Th>
                  <Th style={{ textAlign: 'right' }}>Received</Th>
                  <Th style={{ textAlign: 'right' }}>Pending</Th>
                </tr>
              </thead>
              <tbody>
                {(po.lines || []).map((line, i) => (
                  <Tr key={i}>
                    <Td>{i + 1}</Td><Td>{line.description}</Td>
                    <Td style={{ textAlign: 'right' }}>{line.quantity} {line.unitOfMeasure && <span style={{ fontSize: '11px', color: '#64748B' }}>{line.unitOfMeasure}</span>}<TwoUnitNote line={line} /></Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(line.unitPrice)}</Td>
                    <Td style={{ textAlign: 'right' }}>{line.discountPercent || 0}%</Td>
                    <Td style={{ textAlign: 'right' }}>{line.taxable ? formatAmount(line.taxAmount) : '-'}</Td>
                    <Td style={{ textAlign: 'right' }}>{formatAmount(line.lineTotal)}</Td>
                    <Td style={{ textAlign: 'right' }}>{line.receivedQty || 0}</Td>
                    <Td style={{ textAlign: 'right' }}>{po.status === 'Closed'
                      ? (() => { const left = Math.max(0, (Number(line.quantity) || 0) - (Number(line.receivedQty) || 0)); return left > 0 ? <span style={{ color: '#546E7A' }}>{left} closed</span> : 0; })()
                      : (line.pendingQty ?? (line.quantity - (line.receivedQty || 0)))}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
          <TotalsSection>
            <TotalsTable>
              <tbody>
                <LineDiscountRows lines={po.lines} />
                <tr><td>Subtotal</td><td>{formatAmount(po.subtotal)}</td></tr>
                {po.discountAmount > 0 && <tr><td>Discount</td><td>-{formatAmount(po.discountAmount)}</td></tr>}
                <tr><td>Tax</td><td>{formatAmount(po.taxAmount)}</td></tr>
                <TotalRow><td>Total ({po.currency})</td><td>{formatAmount(po.totalAmount)}</td></TotalRow>
              </tbody>
            </TotalsTable>
          </TotalsSection>
        </FormCard>

        {(po.notes || po.termsConditions) && (
          <FormCard>
            {po.notes && <><SectionTitle>Notes</SectionTitle><NotesText>{po.notes}</NotesText></>}
            {po.termsConditions && <><SectionTitle style={{ marginTop: spacing.md }}>Terms & Conditions</SectionTitle><NotesText>{po.termsConditions}</NotesText></>}
          </FormCard>
        )}

        <AttachmentPanel transactionId={po?.id} attachment={po?.attachment} canEdit={false} />
      </>
    );
  };

  const renderFormView = () => (
    <>
      <FormHeader>
        <BackButton onClick={() => { setViewMode('list'); resetForm(); }}>
          <FiArrowLeft /> Back to List
        </BackButton>
        <FormTitle>{editingPO ? `Edit: ${editingPO.docNumber}` : 'New Purchase Order'}</FormTitle>
        <FormActions>
          <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}>
            <FiX /> Cancel
          </SaveButton>
          {canEdit && (
            <SaveButton onClick={() => handleSave('Draft')} disabled={saving}>
              <FiSave /> {saving ? 'Saving...' : 'Save Draft'}
            </SaveButton>
          )}
          {canEdit && (formData.status === 'Draft' || !editingPO) && (
            <SaveButton $approve onClick={() => handleSave('Approved')} disabled={saving}>
              <FiCheck /> Save & Approve
            </SaveButton>
          )}
        </FormActions>
      </FormHeader>

      <FormCard>
        <SectionTitle>Order Details</SectionTitle>
        <FormGrid>
          <FormGroup>
            <Label>PO Number</Label>
            <Input value={editingPO?.docNumber || 'Auto-generated'} disabled />
          </FormGroup>
          <FormGroup>
            <Label>PO Date *</Label>
            <Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>Status</Label>
            <Input value={formData.status} disabled />
          </FormGroup>
          <FormGroup>
            <Label>Currency</Label>
            <Input value={formData.currency} disabled />
          </FormGroup>
          <FormGroup $span2>
            <Label>Link to Requisition</Label>
            <Select
              value={formData.parentId}
              onChange={e => handleRequisitionChange(e.target.value)}
            >
              <option value="">-- Direct PO (no requisition) --</option>
              {approvedRequisitions.map(req => (
                <option key={req.id} value={req.id}>
                  {req.docNumber} — {req.requestedBy || ''}
                  {req.department ? ` (${req.department})` : ''}
                </option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup $span2>
            <Label>Vendor *</Label>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <VendorSelect
                  vendors={vendors}
                  selectedVendorId={formData.vendorId}
                  onChange={handleVendorChange}
                  balanceOnDemand
                />
              </div>
              <button type="button" title="Quick add new vendor"
                onClick={() => setShowQuickAddVendor(true)}
                style={{ flexShrink: 0, width: 26, height: 26, border: '1px solid #ccc', borderRadius: 6, background: '#fff', color: '#800020', fontSize: 16, fontWeight: 700, cursor: 'pointer', lineHeight: 1 }}>+</button>
            </div>
            {formData.parentId && !formData.vendorId && (
              <small style={{ color: colors.warning?.main || '#b45309', fontSize: 11, marginTop: 4 }}>
                The selected requisition has mixed (or no) preferred vendors — pick one explicitly.
              </small>
            )}
          </FormGroup>
          <FormGroup>
            <Label>Reference No.</Label>
            <Input value={formData.referenceNo} onChange={e => handleHeaderChange('referenceNo', e.target.value)} placeholder="Vendor quotation ref" />
          </FormGroup>
          <FormGroup>
            <Label>Delivery Date</Label>
            <Input type="date" value={formData.deliveryDate} onChange={e => handleHeaderChange('deliveryDate', e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>Payment Terms</Label>
            <Input value={formData.paymentTerms} onChange={e => handleHeaderChange('paymentTerms', e.target.value)} placeholder="e.g., 30 days" />
          </FormGroup>
          <FormGroup>
            <Label>Delivery Address</Label>
            <Input value={formData.deliveryAddress} onChange={e => handleHeaderChange('deliveryAddress', e.target.value)} placeholder="Delivery location" />
          </FormGroup>
          <FormGroup>
            <Label>Employee</Label>
            <Select value={formData.employeeId} onChange={e => {
              const emp = employees.find(em => em.id === e.target.value);
              handleHeaderChange('employeeId', e.target.value);
              handleHeaderChange('employeeName', emp ? `${emp.firstName || ''} ${emp.lastName || ''}`.trim() : '');
            }}>
              <option value="">-- Select Employee --</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.employeeCode ? emp.employeeCode + ' - ' : ''}{emp.firstName} {emp.lastName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Job</Label>
            <Select value={formData.jobId} onChange={e => {
              const job = jobs.find(j => j.id === e.target.value);
              handleHeaderChange('jobId', e.target.value);
              handleHeaderChange('jobName', job?.jobName || '');
            }}>
              <option value="">-- Select Job --</option>
              {jobs.map(job => (
                <option key={job.id} value={job.id}>{job.jobCode ? job.jobCode + ' - ' : ''}{job.jobName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>{reqLabel(activeCompany, 'Location')}</Label>
            <Select value={formData.locationId} onChange={e => {
              const loc = locations.find(l => l.id === e.target.value);
              handleHeaderChange('locationId', e.target.value);
              handleHeaderChange('locationName', loc?.locationName || '');
            }}>
              <option value="">-- Select Location --</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.locationName}</option>
              ))}
            </Select>
          </FormGroup>
        </FormGrid>
      </FormCard>

      <FormCard>
        <LineItemsHeader>
          <SectionTitle style={{ margin: 0 }}>Line Items</SectionTitle>
          <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>
        </LineItemsHeader>

        <LineTableContainer>
          <LineTable>
            <thead>
              <tr>
                <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh>
                <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Item</LineTh>
                <LineTh style={{ width: 260, minWidth: 260, maxWidth: 260 }}>Description</LineTh>
                <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Qty</LineTh>
                <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Unit Price</LineTh>
                <LineTh style={{ width: 100, minWidth: 100, maxWidth: 100 }}>Disc % / Amt</LineTh>
                <LineTh style={{ width: 80, minWidth: 80, maxWidth: 80 }}>Tax?</LineTh>
                <LineTh style={{ width: 100, minWidth: 100, maxWidth: 100 }}>Tax %</LineTh>
                <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Total</LineTh>
                {lineItems.some(l => l.trackingType === 'Serial') && <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Serial Numbers</LineTh>}
                <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((line, index) => {
                const calc = calculateLine(line);
                return (
                  <tr key={index}>
                    <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                    <LineTd>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <ItemSelect ref={(el) => { lineItemRefs.current[index] = el; }} items={items} selectedItemId={line.itemId} onChange={(itemId) => handleLineChange(index, 'itemId', itemId)} stockBalances={stockBalances} stockOnDemand onRequestStock={requestItemStock} />
                        </div>
                        <button type="button" title="Quick add new item"
                          onClick={() => { setQuickAddIndex(index); setShowQuickAddItem(true); }}
                          style={{ flexShrink: 0, width: 26, height: 26, border: '1px solid #ccc', borderRadius: 6, background: '#fff', color: '#800020', fontSize: 16, fontWeight: 700, cursor: 'pointer', lineHeight: 1 }}>+</button>
                      </div>
                    </LineTd>
                    <LineTd>
                      <LineInput value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)} placeholder="Item description" />
                    </LineTd>
                    <LineTd>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <LineInput type="number" min="0" value={line.quantity} onChange={e => handleLineChange(index, 'quantity', Number(e.target.value))} style={{ textAlign: 'right' }} />
                        <LineUnitPicker
                          item={items.find(i => i.id === line.itemId)}
                          line={line}
                          onChange={(uc) => handleLineChange(index, 'unitCode', uc)}
                        />
                      </div>
                      <TwoUnitQtyBox item={items.find(i => i.id === line.itemId)} unitCode={line.unitCode || line.unitOfMeasure}
                        quantity={line.quantity} hasSize={line.hasSize}
                        onChange={(q) => handleLineChange(index, 'quantity', q)} />
                    </LineTd>
                    <LineTd>
                      <LineInput type="number" min="0" step="0.01" value={line.unitPrice} onChange={e => handleLineChange(index, 'unitPrice', Number(e.target.value))} style={{ textAlign: 'right' }} />
                    </LineTd>
                    <LineTd>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <LineInput type="number" min="0" max="100" step="0.01" value={line.discountPercent}
                          onChange={e => handleLineChange(index, 'discountPercent', Number(e.target.value))} style={{ textAlign: 'right' }} title="Discount %" placeholder="%" />
                        <LineInput type="number" min="0" step="0.01" value={line.discountAmount || 0}
                          onChange={e => handleLineChange(index, 'discountAmountInput', Number(e.target.value))} style={{ textAlign: 'right' }} title="Discount amount" placeholder="Amt" />
                      </div>
                    </LineTd>
                    <LineTd style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={line.taxable} onChange={e => handleLineChange(index, 'taxable', e.target.checked)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !line.taxable) handleLastFieldEnter(e, index); }} />
                    </LineTd>
                    <LineTd>
                      <LineInput type="number" min="0" max="100" value={line.taxRate} onChange={e => handleLineChange(index, 'taxRate', Number(e.target.value))}
                        onKeyDown={(e) => handleLastFieldEnter(e, index)}
                        disabled={!line.taxable} style={{ textAlign: 'right', opacity: line.taxable ? 1 : 0.4 }} />
                    </LineTd>
                    <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(calc.lineTotal)}</LineTd>
                    {lineItems.some(l => l.trackingType === 'Serial') && (
                      <LineTd>{line.trackingType === 'Serial' ? <LineInput value={line.serialText !== undefined ? line.serialText : (line.serialNumbers || []).join(', ')} onChange={e => handleLineChange(index, 'serialText', e.target.value)} onBlur={e => { const parsed = e.target.value.split(',').map(s => s.trim()).filter(Boolean); handleLineChange(index, 'serialNumbers', parsed); handleLineChange(index, 'serialText', undefined); }} placeholder={`${line.quantity || 0} serial(s), comma-separated`} /> : <span style={{ color: colors.text.secondary }}>-</span>}</LineTd>
                    )}
                    <LineTd style={{ textAlign: 'center' }}>
                      {lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)} title="Remove"><FiX /></RemoveLineBtn>}
                    </LineTd>
                  </tr>
                );
              })}
            </tbody>
          </LineTable>
        </LineTableContainer>

        <TotalsSection>
          <TotalsTable>
            <tbody>
              <LineDiscountRows lines={lineItems.map(calculateLine)} />
              <tr><td>Subtotal</td><td>{formatAmount(totals.subtotal)}</td></tr>
              <tr>
                <td>Discount</td>
                <td><DiscountInput type="number" min="0" step="0.01" value={formData.discountAmount} onChange={e => handleHeaderChange('discountAmount', Number(e.target.value))} /></td>
              </tr>
              <tr><td>Tax</td><td>{formatAmount(totals.taxAmount)}</td></tr>
              <TotalRow><td>Total ({formData.currency})</td><td>{formatAmount(totals.totalAmount)}</td></TotalRow>
            </tbody>
          </TotalsTable>
        </TotalsSection>
      </FormCard>

      <FormCard>
        <SectionTitle>Additional Information</SectionTitle>
        <FormGrid>
          <FormGroup $span2>
            <Label>Notes</Label>
            <TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Internal notes..." rows={3} />
          </FormGroup>
          <FormGroup $span2>
            <Label>Terms & Conditions</Label>
            <TextArea value={formData.termsConditions} onChange={e => handleHeaderChange('termsConditions', e.target.value)} placeholder="Terms and conditions for vendor..." rows={3} />
          </FormGroup>
        </FormGrid>
      </FormCard>

      <AttachmentPanel
        transactionId={editingPO?.id}
        attachment={editingPO?.attachment}
        canEdit={canEdit}
        onChange={({ attachment }) => setEditingPO(prev => prev ? { ...prev, attachment } : prev)}
      />
    </>
  );

  useDocumentShortcuts({
    onNew: canEdit ? handleCreate : undefined,
    onSave: canEdit ? () => handleSave('Approved') : undefined,
    viewMode,
    saving
  });

  return (
    <PageLayout pageTitle="Purchase Order" breadcrumb="Purchases / Purchase Order">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
      {showQuickAddItem && (
        <QuickAddItemModal
          companyId={activeCompany?.id}
          existingItems={items}
          onCreated={handleQuickItemCreated}
          onClose={() => { setShowQuickAddItem(false); setQuickAddIndex(null); }}
        />
      )}
      {showQuickAddVendor && (
        <QuickAddVendorModal
          companyId={activeCompany?.id}
          existingVendors={vendors}
          onCreated={handleQuickVendorCreated}
          onClose={() => setShowQuickAddVendor(false)}
        />
      )}
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS (unique to this file) ============

const LinkedBadge = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 10px;
  font-size: ${typography.fontSize.xs}; font-weight: ${typography.fontWeight.medium};
  color: ${colors.info.main}; background: ${colors.info.main}15;
  svg { font-size: 12px; }
`;

export default PurchaseOrder;
